import { CustomAxios } from "@shared/api/axiosClient";

import type { Schemas } from "@shared/api/types";

// Capa HTTP del dominio timeentry. Funciones puras: entran params, sale
// Promise<data>. La auth la adjunta el interceptor axios compartido;
// no incluir helpers de config en esta capa (el interceptor los reemplaza).

export type MissingHoursWeeklySummary = Schemas["MissingHoursWeeklySummaryDTO"];
export type MonthlySummaryHours = Schemas["MonthlySummaryHoursDTO"];
export type ProjectsWithAssignmentsResponse = Schemas["ProjectsWithAssignmentsResponseDTO"];
export type ProjectWithAssignment = Schemas["ProjectWithAssigmentDTO"];
export type Activity = Schemas["ActivityDTO"];
export type AssignedProject = Schemas["AssignedProjectDTO"];
export type Issue = Schemas["IssueDTO"];
export type ValidateChargedHourInWeek = Schemas["ValidateChargedHourInWeekDTO"];
export type ValidateChargedHourInMonth = Schemas["ValidateChargedHourInMonthDTO"];

/**
 * Los ids de estos payloads llegan como string: el `<Select>` de MUI entrega el
 * id numérico del `<MenuItem>` y el `yup.string()` del schema lo castea a string
 * al validar, aunque las DTO generadas (`CreateTimeEntryDTO`, `ChargeHourDTO`,
 * `ChargeHourJiraDTO`) declaren projectId/issueId/activityId numéricos. Comportamiento preexistente; esta migración es cero-cambio-de-
 * comportamiento, así que se tipa tal cual llega en vez de forzar el tipo que
 * pide el contrato.
 */
export interface CreateTimeEntryPayload {
  projectId: number | string;
  activityId: string;
  assignedProjectId?: string;
  issueId: number;
  spentOn: string | Date;
  hours: number;
  comment: string;
}

export interface ChargeHoursPayload {
  projectId: string;
  typeOfCharge?: string;
  hoursPerDay?: number;
  hourProductive?: number;
  hourPaidLeave?: number;
  hourDisease?: number;
  hourUnpaidLeave?: number;
  total?: number;
  issueId: number;
  type: string;
  localDate: string;
}

export interface ChargeHoursJiraPayload {
  projectId: string;
  issueId: string;
  hoursPerDay: number;
}

/**
 * `time-entry/charge-hour` no existe en el contrato generado ni en el backend:
 * solo existen `charge-hours` y `charge-hours/jira`, ambos autenticados. Esta
 * ruta pública pega a un 404 hoy — hallazgo preexistente, fuera de alcance de
 * esta migración tipo-only. El request se tipa tal cual lo arman los dos call
 * sites (ChargeHourPublicPage, ChargeHourForm), sin inventar un endpoint de
 * contrato que no existe.
 */
export interface ChargeHourPublicRequest {
  id?: string;
  token?: string;
  projectName?: string;
  localDate?: string;
  type?: string;
  hourProductive?: number | string;
  hourVacation?: number | string;
  hourDisease?: number | string;
  hourOther?: number | string;
}

/** Obtiene el resumen de horas faltantes del colaborador. */
export const getMissingHoursSummary = (): Promise<MissingHoursWeeklySummary[]> =>
  CustomAxios.get("time-entry/missing-hours-summary").then(({ data }) => data);

/** Obtiene el resumen mensual de horas por proyecto. */
export const getMonthlySummaryHours = (): Promise<MonthlySummaryHours[]> =>
  CustomAxios.get("time-entry/monthly-summary-hours").then(({ data }) => data);

/** Obtiene proyectos con asignaciones del colaborador (para localStorage). */
export const getProjectsWithAssignments = (): Promise<ProjectsWithAssignmentsResponse> =>
  CustomAxios.get("projects/with-assignments").then(({ data }) => data);

/** Obtiene proyectos con asignaciones Jira del colaborador. */
export const getProjectsWithAssignmentsJira = (): Promise<ProjectWithAssignment[]> =>
  CustomAxios.get("projects/with-assignments/jira").then(({ data }) => data);

/** Obtiene el catálogo de actividades de imputación. */
export const getActivities = (): Promise<Activity[]> =>
  CustomAxios.get("time-entry/activities").then(({ data }) => data);

/** Obtiene los proyectos asignados (estructura / estructura operativa). */
export const getAssignedProjects = (): Promise<AssignedProject[]> =>
  CustomAxios.get("time-entry/assigned-project").then(({ data }) => data);

/** Obtiene las issues de un proyecto Redmine. */
export const getProjectIssues = (projectId: number | string): Promise<Issue[]> =>
  CustomAxios.get(`projects/${projectId}/issues`).then(({ data }) => data);

/** Obtiene las issues de un proyecto Jira. */
export const getProjectIssuesJira = (projectId: number | string): Promise<Issue[]> =>
  CustomAxios.get(`projects/${projectId}/issues/jira`).then(({ data }) => data);

/**
 * Valida si la semana ya fue imputada para el proyecto dado.
 * Devuelve { isChargedHourInWeek: boolean }.
 */
export const validateTimesheetWeekByProject = (
  projectId: number | string
): Promise<ValidateChargedHourInWeek> =>
  CustomAxios.get(
    `time-entry/validate-timesheet/week-by-project/${projectId}`
  ).then(({ data }) => data);

/**
 * Valida si el mes ya fue imputado para el proyecto dado.
 * Devuelve { isChargedHourInMonth: boolean }.
 */
export const validateTimesheetMonthByProject = (
  projectId: number | string
): Promise<ValidateChargedHourInMonth> =>
  CustomAxios.get(
    `time-entry/validate-timesheet/month-by-project/${projectId}`
  ).then(({ data }) => data);

/** Registra una imputación de horas (general / faltantes). */
export const createTimeEntry = (value: CreateTimeEntryPayload): Promise<string> =>
  CustomAxios.post("time-entry", value).then(({ data }) => data);

/**
 * Carga horas desde el link público (automático o manual por form).
 * Se usa tanto desde la página pública como desde el formulario de carga manual.
 */
export const chargeHourPublic = (request: ChargeHourPublicRequest): Promise<unknown> =>
  CustomAxios.post("time-entry/charge-hour", request).then(({ data }) => data);

/** Carga horas de forma automática (semana / mes) o descriptiva (manual). */
export const chargeHours = (value: ChargeHoursPayload): Promise<string> =>
  CustomAxios.post("time-entry/charge-hours", value).then(({ data }) => data);

/** Carga horas mediante issues de Jira. */
export const chargeHoursJira = (value: ChargeHoursJiraPayload): Promise<string> =>
  CustomAxios.post("time-entry/charge-hours/jira", value).then(({ data }) => data);

/** Actualiza el registro de horas mensual existente. */
export const updateMonthlyHours = (value: MonthlySummaryHours): Promise<MonthlySummaryHours> =>
  CustomAxios.put("time-entry/update-monthly-hours", value).then(({ data }) => data);
